import { Logger, LoggerService } from '@Shared/logger';
import { APP_EVENTS } from '@Core';
import { BLUECONIC_PROFILE_PROPERTIES } from '../_models/blueconic.constants';
import { BlueConicReadinessService } from './blueconic-readiness.service';
import { Bus, OnAuthDeterminedResult } from '@Modules/event_module';

export class BCProfileSyncService {
  private static instance: BCProfileSyncService;

  static get Instance(): BCProfileSyncService {
    if (this.instance === null || this.instance === undefined) {
      this.instance = new BCProfileSyncService();
    }
    return this.instance;
  }

  private Logger: Logger = LoggerService.createLogger('BCProfileSyncService');
  private initialized = false;

  constructor() {
    this.Logger.trace('****  Blueconic Profile Sync CTOR ****');
  }

  /**
   * Writes the auth state to the BC profile on every onAuthDetermined.
   */
  public setup(): void {
    if (this.initialized) return;
    this.initialized = true;
    Bus.on(APP_EVENTS.onAuthDetermined, (result) => {
      // An errored auth check doesn't know the real state; don't tell BC the reader is anonymous.
      if (!result || result.error) return;
      // Not awaited: BlueConic readiness can take seconds and must not hold up the pipeline.
      void this.syncAuthProfile(result);
    });
    this.Logger.trace('BCProfileSyncService listening for onAuthDetermined');
  }

  /**
   * Never throws: callers fire-and-forget from the pipeline, so a missing or
   * slow BlueConic must not surface as an unhandled rejection.
   */
  public async syncAuthProfile(
    authDetermined: OnAuthDeterminedResult,
  ): Promise<boolean> {
    try {
      const isReady = await BlueConicReadinessService.Instance.blueConicReady();
      if (!isReady || !window.blueConicClient?.profile) {
        this.Logger.trace('BC not ready, skipping profile sync');
        return false;
      }

      const bcProfile = window.blueConicClient.profile;
      const profile = bcProfile.getProfile();

      // Property id -> value, exactly as written to BC.
      const values: Record<string, string> = {
        [BLUECONIC_PROFILE_PROPERTIES.loggedInStatus]:
          authDetermined.isAuthenticated ? 'true' : 'false',
      };
      // Only write identity fields when known, so an anonymous visit keeps BC's stored values.
      const auth0Id = authDetermined.user?.auth0Id;
      const email = authDetermined.user?.email;
      const mobileLogin = authDetermined.user?.metadata?.mobile_login;
      if (auth0Id) {
        BLUECONIC_PROFILE_PROPERTIES.auth0Ids.forEach((key) => {
          values[key] = auth0Id;
        });
      }
      if (email) values[BLUECONIC_PROFILE_PROPERTIES.email] = email;
      if (mobileLogin) {
        BLUECONIC_PROFILE_PROPERTIES.mobileLogins.forEach((key) => {
          values[key] = mobileLogin;
        });
      }

      Object.entries(values).forEach(([key, value]) =>
        profile.setValue(key, value),
      );
      bcProfile.updateProfile(null, () =>
        this.Logger.trace('BC profile update persisted'),
      );
      this.Logger.info('Synced auth state to BC profile', values);
      return true;
    } catch (error) {
      this.Logger.warn('Failed to sync auth state to BC profile', error);
      return false;
    }
  }
}
